import type { ParserBlock } from '@vif/parsers';
import type { VifDB } from './db.js';
import type { ContentIdentity } from './hash.js';
import type {
  BlockRow,
  BookmarkRow,
  DocumentFormat,
  PositionRow,
  SessionRow,
  StoredDocument,
} from './types.js';

/**
 * Domain CRUD over the PRD §10 stores. Policy (5-slot cap, duplicate
 * prompts, sorting) belongs to the shell — this layer only guarantees
 * integrity: atomic writes, cascade deletes, PD-9 identity on add.
 */

export interface AddDocumentInput {
  readonly title: string;
  readonly format: DocumentFormat;
  readonly lang: string;
  readonly blocks: readonly ParserBlock[];
  readonly wordCount: number;
  /**
   * PD-9 identity, computed from the raw bytes BEFORE parsing — pdf.js
   * transfers the ArrayBuffer to its worker, so the bytes may be detached
   * by the time the document is stored (and hashing first lets shells
   * detect duplicates without parsing at all).
   */
  readonly identity: ContentIdentity;
  readonly now?: number;
}

export async function addDocument(db: VifDB, input: AddDocumentInput): Promise<StoredDocument> {
  const { contentHash, size } = input.identity;
  const document: StoredDocument = {
    id: crypto.randomUUID(),
    title: input.title,
    format: input.format,
    lang: input.lang,
    wordCount: input.wordCount,
    addedAt: input.now ?? Date.now(),
    contentHash,
    size,
  };
  const tx = db.transaction(['documents', 'blocks'], 'readwrite');
  void tx.objectStore('documents').put(document);
  const blocks = tx.objectStore('blocks');
  input.blocks.forEach((block, idx) => {
    const row: BlockRow = {
      docId: document.id,
      idx,
      kind: block.kind,
      level: block.level ?? null,
      text: block.text,
    };
    void blocks.put(row);
  });
  await tx.done;
  return document;
}

export function getDocument(db: VifDB, id: string): Promise<StoredDocument | undefined> {
  return db.get('documents', id);
}

export function listDocuments(db: VifDB): Promise<StoredDocument[]> {
  return db.getAll('documents');
}

/** PD-9 consumer: same content (hash AND size) → same library entry. */
export async function findDocumentByIdentity(
  db: VifDB,
  contentHash: string,
  size: number,
): Promise<StoredDocument | undefined> {
  const candidates = await db.getAllFromIndex('documents', 'by-contentHash', contentHash);
  return candidates.find((doc) => doc.size === size);
}

export async function setLangOverride(
  db: VifDB,
  id: string,
  langOverride: string | undefined,
): Promise<void> {
  const doc = await db.get('documents', id);
  if (doc === undefined) return;
  const next: StoredDocument = { ...doc };
  if (langOverride === undefined) {
    delete (next as { langOverride?: string }).langOverride;
  } else {
    (next as { langOverride?: string }).langOverride = langOverride;
  }
  await db.put('documents', next);
}

/** Cascade: blocks, position, bookmarks die with the document. Sessions are
 * historical stats and survive (their docId is optional by schema). */
export async function deleteDocument(db: VifDB, id: string): Promise<void> {
  const tx = db.transaction(['documents', 'blocks', 'positions', 'bookmarks'], 'readwrite');
  void tx.objectStore('documents').delete(id);
  void tx.objectStore('positions').delete(id);
  const blockKeys = await tx.objectStore('blocks').index('by-doc').getAllKeys(id);
  for (const key of blockKeys) void tx.objectStore('blocks').delete(key);
  const bookmarkKeys = await tx.objectStore('bookmarks').index('by-doc').getAllKeys(id);
  for (const key of bookmarkKeys) void tx.objectStore('bookmarks').delete(key);
  await tx.done;
}

/** Blocks in document order, rehydrated to the parser Block shape. */
export async function getBlocks(db: VifDB, docId: string): Promise<ParserBlock[]> {
  const rows = await db.getAllFromIndex('blocks', 'by-doc', docId);
  return rows.map((row) =>
    row.kind === 'chapter-heading'
      ? { kind: row.kind, text: row.text, title: row.text, level: row.level ?? undefined }
      : { kind: row.kind, text: row.text },
  );
}

export async function savePosition(
  db: VifDB,
  docId: string,
  wordIdx: number,
  now?: number,
): Promise<void> {
  const row: PositionRow = { docId, wordIdx, updatedAt: now ?? Date.now() };
  await db.put('positions', row);
}

export function getPosition(db: VifDB, docId: string): Promise<PositionRow | undefined> {
  return db.get('positions', docId);
}

export async function addBookmark(
  db: VifDB,
  docId: string,
  wordIdx: number,
  label: string,
  now?: number,
): Promise<void> {
  const row: BookmarkRow = { docId, wordIdx, label, createdAt: now ?? Date.now() };
  await db.put('bookmarks', row);
}

export async function removeBookmark(db: VifDB, docId: string, wordIdx: number): Promise<void> {
  await db.delete('bookmarks', [docId, wordIdx]);
}

export function getBookmarks(db: VifDB, docId: string): Promise<BookmarkRow[]> {
  return db.getAllFromIndex('bookmarks', 'by-doc', docId);
}

export async function appendSession(
  db: VifDB,
  session: Omit<SessionRow, 'id'>,
): Promise<SessionRow> {
  const row: SessionRow = { id: crypto.randomUUID(), ...session };
  await db.put('sessions', row);
  return row;
}

export function listSessions(db: VifDB): Promise<SessionRow[]> {
  return db.getAll('sessions');
}
